/**
 * @file TwoFactorVerification.jsx
 * @description Handles two-factor authentication verification during login
 *
 * @section Responsibilities
 * - Handle 2FA token input during login
 * - Verify TOTP codes
 * - Support backup code verification
 * - Handle verification errors
 * - Continue login process after verification
 *
 * @section Dependencies
 * - Request: API communication
 * - OtpInput: Token input component
 * - Session utilities
 *
 * @section Integration Points
 * - Authentication API: Token verification
 * - Login flow: Continue after verification
 * - Session management
 *
 * @section State Management
 * - token: User input 2FA token
 * - useBackupCode: Toggle backup code mode
 * - loading: Verification process status
 * - error: Verification error messages
 *
 * @section Events
 * - onVerify: Verify 2FA token
 * - onBackupVerify: Verify backup code
 * - onCancel: Cancel verification
 * - onSuccess: Successful verification
 *
 * @section Props
 * @prop {Object} credentials - Login credentials
 * @prop {Function} onSuccess - Success callback
 * @prop {Function} onCancel - Cancel callback
 */

import React, { useState } from "react";
import { Box, Typography, Alert, Link } from "@mui/material";
import OtpInput from "react-otp-input";
import DgButton from "../../components/DgButton";
import Request from "../../utils/fetchUtils";
import { validateOnboarding } from "../../utils/login";
import { useNavigate } from "react-router-dom";
import "./../../styles/two-factor.css";

const TwoFactorVerification = ({ credentials, onSuccess, onCancel, setUserRole }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const verifyTwoFactor = async () => {
    const codeToVerify = (useBackupCode ? backupCode : token).trim();

    if (!codeToVerify || (useBackupCode ? codeToVerify.length < 6 : codeToVerify.length !== 6)) {
      setError(useBackupCode ? "Please enter a valid backup code" : "Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Attempt login with 2FA token
      const loginData = {
        ...credentials,
        username: credentials.email.toLowerCase(),
        twoFactorToken: codeToVerify,
      };

      const res = await Request("/user/auth", loginData, "POST");
      
      if (res.ok) {
        const { userData, token: authToken, accessToken } = res.data;
        setUserRole(userData.role);
        localStorage.setItem("jwt", authToken);
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("session", JSON.stringify(userData));
        
        Object.keys(userData).forEach((k) => {
          const value = userData[k];
          const parsedValue = typeof value === "object" ? JSON.stringify(value) : value;
          localStorage.setItem(k, parsedValue);
        });
        
        if (onSuccess) onSuccess(userData);
        validateOnboarding(userData, navigate);
      } else {
        setError(
          res.message ||
            (useBackupCode ? "Invalid backup code" : "Invalid verification code")
        );
      }
    } catch (error) {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTokenChange = (value) => {
    setToken(value);
    setError("");
  };

  const handleBackupCodeChange = (event) => {
    // Backup codes are 8-char uppercase alphanumeric — normalize as the user types
    setBackupCode(event.target.value.toUpperCase().replace(/\s/g, ""));
    setError("");
  };

  const toggleBackupMode = () => {
    setUseBackupCode(!useBackupCode);
    setToken("");
    setBackupCode("");
    setError("");
  };  return (
    <Box sx={{ 
      maxWidth: 400, 
      mx: "auto", 
      p: 3, 
      backgroundColor: "#021C2D",
      borderRadius: "12px",
      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
      border: "1px solid #274A61",
      position: "relative",
      zIndex: 10
    }} className="two-factor-verification">
      <Typography variant="h5" gutterBottom align="center" sx={{ color: "#eeeeee" }} className="two-factor-title">
        Two-Factor Authentication
      </Typography>

      <Typography variant="body2" align="center" sx={{ mb: 3, color: "#95B2C5" }} className="two-factor-subtitle">
        {useBackupCode 
          ? "Enter one of your backup codes to continue"
          : "Enter the 6-digit code from your authenticator app"
        }
      </Typography>

      {error && (
        <Alert severity="error" sx={{ 
          mb: 2,
          backgroundColor: "rgba(244, 67, 54, 0.1)",
          border: "1px solid #f44336",
          color: "#f44336",
          borderRadius: "8px"
        }}>
          {error}
        </Alert>
      )}

      {useBackupCode ? (
        <Box sx={{ mb: 3 }}>
          <Typography
            component="label"
            htmlFor="backupCode"
            variant="body2"
            sx={{ display: "block", mb: 1, color: "#95B2C5" }}
          >
            Backup Code
          </Typography>
          <input
            id="backupCode"
            type="text"
            name="backupCode"
            value={backupCode}
            onChange={handleBackupCodeChange}
            placeholder="Enter backup code"
            maxLength={8}
            autoComplete="off"
            autoFocus
            className="token-input"
            style={{
              width: "100%",
              height: "45px",
              padding: "0 12px",
              fontSize: "18px",
              letterSpacing: "2px",
              borderRadius: "8px",
              border: "1px solid #274A61",
              outline: "none",
              backgroundColor: "#012B45",
              color: "#eeeeee",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.border = "2px solid #A1BCA8";
              e.target.style.boxShadow = "0 0 0 2px rgba(161, 188, 168, 0.2)";
            }}
            onBlur={(e) => {
              e.target.style.border = "1px solid #274A61";
              e.target.style.boxShadow = "none";
            }}
          />
        </Box>
      ) : (        <Box className="otp-input-container" display="flex" justifyContent="center" sx={{ mb: 3 }}>
          <OtpInput
            value={token}
            onChange={handleTokenChange}
            numInputs={6}
            renderSeparator={<span style={{ width: "8px" }}></span>}
            renderInput={(props) => (
              <input
                {...props}
                className="token-input"
                style={{
                  width: "45px",
                  height: "45px",
                  margin: "0 4px",
                  fontSize: "18px",
                  borderRadius: "8px",
                  border: "1px solid #274A61",
                  textAlign: "center",
                  outline: "none",
                  backgroundColor: "#012B45",
                  color: "#eeeeee"
                }}
                onFocus={(e) => {
                  e.target.style.border = "2px solid #A1BCA8";
                  e.target.style.boxShadow = "0 0 0 2px rgba(161, 188, 168, 0.2)";
                }}
                onBlur={(e) => {
                  e.target.style.border = "1px solid #274A61";
                  e.target.style.boxShadow = "none";
                }}
              />
            )}
          />
        </Box>
      )}      <Box sx={{ mb: 2 }}>
        <DgButton
          category="primary"
          size="large"
          text={loading ? "Verifying..." : "Verify"}
          action={verifyTwoFactor}
          loading={loading}
          disabled={useBackupCode ? !backupCode : token.length !== 6}
          style={{ 
            width: "100%", 
            padding: "16px 32px", 
            fontSize: "16px" 
          }}
        />
      </Box>      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Link
          component="button"
          variant="body2"
          onClick={toggleBackupMode}
          sx={{ 
            textDecoration: "none",
            color: "#A1BCA8",
            "&:hover": {
              color: "#eeeeee"
            }
          }}
        >
          {useBackupCode ? "Use authenticator app" : "Use backup code"}
        </Link>

        <Link
          component="button"
          variant="body2"
          onClick={onCancel}
          sx={{ 
            textDecoration: "none",
            color: "#95B2C5",
            "&:hover": {
              color: "#eeeeee"
            }
          }}
        >
          Cancel
        </Link>
      </Box>

      <Box sx={{ 
        mt: 3, 
        p: 2, 
        backgroundColor: "#012B45", 
        borderRadius: "8px",
        border: "1px solid #274A61"
      }}>
        <Typography variant="caption" sx={{ color: "#95B2C5" }}>
          <strong style={{ color: "#eeeeee" }}>Lost access to your authenticator?</strong><br />
          Use a backup code or contact support for assistance.
        </Typography>
      </Box>
    </Box>
  );
};

export default TwoFactorVerification;
